import type {
  ScreenerAnnouncementsResponse,
  ScreenerMeta,
  ScreenerQueryParams,
  ScreenerStockDetail,
  ScreenerStocksResponse,
} from '@/types/screener';

// The public URL is what we hand to Claude and developers, whatever this build talks to.
export const PUBLIC_SCREENER_API_URL = 'https://hft.draamview.com/api/public/v1';

const screenerBaseUrl = (): string =>
  (process.env.NEXT_PUBLIC_SCREENER_API_URL || PUBLIC_SCREENER_API_URL).replace(/\/$/, '');

export class ScreenerApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ScreenerApiError';
    this.status = status;
  }
}

function buildUrl(endpoint: string, params?: ScreenerQueryParams): string {
  const url = new URL(`${screenerBaseUrl()}/${endpoint.replace(/^\//, '')}`);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === '') return;
      url.searchParams.append(key, String(value));
    });
  }
  return url.toString();
}

async function get<T>(endpoint: string, params?: ScreenerQueryParams): Promise<T> {
  let response: Response;
  try {
    response = await fetch(buildUrl(endpoint, params), {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });
  } catch {
    throw new ScreenerApiError(
      'Could not reach the data server. Check your internet connection and try again.',
      0,
    );
  }

  if (!response.ok) {
    let message = `Request failed (${response.status}).`;
    if (response.status === 429) {
      message = 'Too many requests. Please wait a minute and try again.';
    } else {
      try {
        const body = await response.json();
        if (body?.message) message = body.message;
      } catch {
        // Non-JSON error body; keep the generic message.
      }
    }
    throw new ScreenerApiError(message, response.status);
  }

  return (await response.json()) as T;
}

let metaRequest: Promise<ScreenerMeta> | null = null;

// Meta is the same for every page and changes at most hourly, so one request per visit is enough.
export const getScreenerMeta = (): Promise<ScreenerMeta> => {
  if (!metaRequest) {
    metaRequest = get<ScreenerMeta>('/screener/meta').catch((error) => {
      metaRequest = null;
      throw error;
    });
  }
  return metaRequest;
};

export const getScreenerStocks = (params: ScreenerQueryParams): Promise<ScreenerStocksResponse> =>
  get<ScreenerStocksResponse>('/screener/stocks', params);

export const getScreenerStock = (exchange: string, symbol: string): Promise<ScreenerStockDetail> =>
  get<ScreenerStockDetail>(
    `/screener/stocks/${encodeURIComponent(exchange)}/${encodeURIComponent(symbol)}`,
  );

export const getScreenerAnnouncements = (
  params: ScreenerQueryParams,
): Promise<ScreenerAnnouncementsResponse> =>
  get<ScreenerAnnouncementsResponse>('/screener/announcements', params);
